import { ApolloClient, InMemoryCache, from } from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { Auth } from 'aws-amplify';

import config from 'config';
import { forceLoginRedirect, isAuthSessionError } from 'utils/forceLoginRedirect';

const getGraphQLEndpoint = () => {
  if (process.env.REACT_APP_GRAPHQL_URL) {
    return process.env.REACT_APP_GRAPHQL_URL;
  }
  return 'http://localhost:5001/graphql';
};

// HTTP connection to the API
const httpLink = new HttpLink({
  uri: getGraphQLEndpoint()
});

// Middleware to attach the auth token to every request
const authLink = new SetContextLink(async (prevContext, operation) => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    return {
      headers: {
        ...prevContext.headers,
        Authorization: token ? `Bearer ${token}` : ''
      }
    };
  } catch (error) {
    return {
      headers: {
        ...prevContext.headers
      }
    };
  }
});

// Error handling link
const errorLink = new ErrorLink(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        extensions
      );

      if (isAuthSessionError(message, extensions)) {
        forceLoginRedirect();
      }
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    const statusCode = networkError.statusCode || networkError.status;
    if (statusCode === 401 || statusCode === 403) {
      forceLoginRedirect();
    }
  }
});

// Cache configuration
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        customerCampaignsForCustomer: {
          // Cache separately for different query parameters
          // false = cache based on ALL arguments (first, after, where, order)
          keyArgs: false,
          merge(existing, incoming, { args }) {
            // Don't merge - each query is independent
            // This prevents mixing results from different queries (5 items vs 25 items)
            return incoming;
          }
        }
      }
    },
    Campaign: {
      keyFields: ['id']
    },
    CustomerLogin: {
      fields: {
        customer: {
          // Merge the customer field instead of replacing it
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          }
        }
      }
    },
    CustomerAccount: {
      keyFields: ['id']
    }
  }
});

// Create the Apollo Client
const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all'
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    },
    mutate: {
      errorPolicy: 'all'
    }
  }
});

export default apolloClient;

